import React, { useMemo, useCallback } from 'react';
import { Marker, InfoBox } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';
import { Button } from 'src/components';
import { IconClose } from 'src/icons';
import { useCreateModal, useEditData } from 'src/stores';
import { markerBox } from 'src/images';
import { Trap } from 'src/pages/Home/Home';
import styles from './TrapMarker.module.scss';

type Props = {
  trap: Trap;
  activeTrapId: number | null;
  t_activeMarker: (v: Trap) => void;
  t_closeInfoBox: () => void;
  catch_count: any[];
};

// InfoBox オプション値
const infoBoxOptions = {
  disableAutoPan: true,
  alignBottom: true,
  maxWidth: 270,
  boxClass: styles.infobox,
};

// 備考制限設定
const l_limit = `${process.env.REACT_APP_HOME_REMARKS_LIMIT_LINE}`+"em";

/**
 * TrapMarker コンポーネント
 * @returns React.ReactNode
 */
const TrapMarker: React.FC<Props> = ({ trap, activeTrapId, t_activeMarker, t_closeInfoBox, catch_count }) => {
  /**
   * 害獣座標をメモ化
   */
  const latlng = useMemo(() => new google.maps.LatLng({
    lat: Number(trap.trap_latitude),
    lng: Number(trap.trap_longitude),
  }), [trap]);

  const [cookie] = useCookies();

  /**
   * trap_hunting_name からマーカー画像をセレクト
   * @example
   * <Marker icon={marketFormat([trap_kind_id])} ... />
   */
  const markerFormat = useCallback((kindName: string) => {
    let marker;
    switch (kindName) {
      case '箱罠':
        marker = markerBox;
        break;
      case 'くくり罠':
        marker = markerBox;
        break;
      case 'その他':
        marker = markerBox;
        break;
      case '罠':
        marker = markerBox;
        break;
      default:
        marker = markerBox;
    }
    return marker;
  }, []);

  /**
   * InfoBox を表示するハンドラ
   * @example
   * <Marker onClick={handleMarkerClick} ... />
   */
  const handleMarkerClick = useCallback(() => {
    t_activeMarker(trap);
  }, [t_activeMarker, trap]);

/**
* 情報登録モーダルフラグ
*/
const [createTrapModalOpen] = useCreateModal(state => [state.createTrapModalOpen]);

/**
* 編集モーダル開閉時のデータ格納ストア
*/
const [setEditTrapData] = useEditData(state => [state.setEditTrapData]);

/**
* 害獣情報修正モーダル を表示するハンドラ
* @example
* <Button onClick={handleEditModal} ... />
*/
const handleEditModal = useCallback(() => {
  setEditTrapData(trap);
  createTrapModalOpen();
}, [trap]);

//クリップボードにコピー関数
const copyToClipboard = useCallback(async () => {
  const url = window.location.origin +"/?corporate="+trap.client_code+"&S_lat="+trap.trap_latitude+"&S_lon="+trap.trap_longitude
  await global.navigator.clipboard.writeText(url);
  alert("URLをコピー")
},[trap]);

  return (
    <>

      <Marker
        position={latlng}
        icon={{
          url: markerFormat(trap.trap_hunting_name),
          //anchor: new google.maps.Point(20, 20),
          anchor: new google.maps.Point(15, 15),
        }}
        onClick={handleMarkerClick}
        onRightClick={copyToClipboard}/*230915追記*/
        onDblClick={copyToClipboard}
        zIndex={trap.trap_id === activeTrapId ? 100 : 0}
      />
      {trap.trap_id === activeTrapId && (
        <InfoBox
          position={latlng}
          options={{
            ...infoBoxOptions,
            pixelOffset: new window.google.maps.Size(-135, -10),
          }}
        >
          <div className={styles.overview}>
            <button
              className={styles.closeButton}
              aria-label="詳細ボックスを閉じる"
              onClick={t_closeInfoBox}
            >
              <IconClose />
            </button>
            <ul>
              <li>{trap.trap_id && `罠ID: ${trap.trap_id}`}</li>
              <li>{trap.trap_hunting_name && `罠情報: ${trap.trap_hunting_name}`}</li>
              <li>{trap.vermin_kind_name && `対象害獣: ${trap.vermin_kind_name}`}</li>{/* 2025/04/18 Add Uduki */}
              <li>{trap.trap_admin && `罠管理者: ${trap.trap_admin}`}</li>
              <li>{trap.trap_admin_tel && `電話番号: ${trap.trap_admin_tel}`}</li>
              <li>{trap.trap_start && `設置日: ${dayjs(trap.trap_start).format('YYYY年MM月DD日')}`}</li>
              <li>{trap.trap_stop && `回収日: ${dayjs(trap.trap_stop).format('YYYY年MM月DD日')}`}</li>
              <li>{trap.trap_result && `捕獲結果: ${trap.trap_result}`}</li>
              <li>{trap.trap_address && `場所: ${trap.trap_address}`}</li>
              <li>{trap.camera_id && `撮影カメラID: ${trap.camera_id}`}</li>
              <li className={styles.remarks_hidden} style={{maxHeight: l_limit}}>{trap.trap_remarks && `備考: ${trap.trap_remarks}`}</li>
              <li>{trap.trap_log_latitude && `過去の配置場所:(${trap.trap_log_latitude},${trap.trap_log_longitude})`}</li>
              <li>{catch_count.length !== 0 && `撮影頭数: ${catch_count[0].catch_count}頭`}</li>
            </ul>
            <div className={`${cookie?.verminData?.user_kind_id > 2 ? styles.row : styles.row2}`}>
              <Button onClick={handleEditModal} disabled={!cookie?.verminData?.user_kind_id || trap.client_code!==cookie?.verminData?.client_code || cookie?.verminData?.user_kind_id === 1 || (cookie?.verminData?.user_kind_id === 2 && cookie?.verminData?.user_info_id !== trap.update_user_id)}>
                情報編集
              </Button>
            </div>
          </div>
        </InfoBox>
      )}
    </>
  );
};

export default React.memo(TrapMarker);
